/* ============================================================
   ROOT CAUSES — many findings (criteria) collapse into one cause
   with ONE deliverable. The report dedups + badges counts on
   causes, not findings. Priority = max severity among its member
   findings. The deliverable is the single FIX; per-criterion
   "action" is ignored in the deduped render.
   Ported from the prototype's ROOT_CAUSES.
   ============================================================ */

export interface RootCause {
  id: string;
  label: string;
  /** Criterion ids that roll up into this cause. */
  members: string[];
  /** The single deliverable that resolves the whole cause. */
  deliverable: string;
}

export const ROOT_CAUSES: RootCause[] = [
  {
    id: 'RC-TRAILER-MISSING',
    label: 'No purpose-built channel trailer',
    members: ['A4', 'B1', 'B2', 'F1'],
    deliverable:
      'Produce & pin a 60–90s channel trailer: brand ID in the first 5s, name what the channel delivers and for whom, end on an explicit subscribe CTA. Set it as the non-subscriber featured video.',
  },
  {
    id: 'RC-TITLES-NOT-SEARCHABLE',
    label: 'Titles aren’t searchable or front-loaded',
    members: ['D1', 'D2'],
    deliverable:
      'Rewrite titles to lead with the searchable book/topic keyword in the first ~40 characters before the emotional hook; drop appended channel handles.',
  },
  {
    id: 'RC-THUMBS-WEAK',
    label: 'Thumbnails aren’t consistent or clickable',
    members: ['C1', 'C2', 'C3'],
    deliverable:
      'Retrofit thumbnails to one template: expressive face or vivid scene + 2–4 word hook + consistent brand bar, applied across the recent set.',
  },
  {
    id: 'RC-ABOUT-WEAK',
    label: 'Value proposition is buried',
    members: ['A1', 'A3'],
    deliverable:
      'Rewrite the About/first-impression to lead benefit-first (format + who it’s for) with an explicit subscribe line before the app CTA.',
  },
  {
    id: 'RC-BANNER-SAFEAREA',
    label: 'Banner not optimised for mobile safe-area',
    members: ['A2'],
    deliverable:
      'Recompose the banner so headline + CTAs sit inside the mobile safe zone and are legible at thumbnail scale.',
  },
  {
    id: 'RC-DESC-NO-HOOK-CTA',
    label: 'Descriptions lack hook, structure & CTA',
    members: ['D3'],
    deliverable:
      'Add a question-hook opening, a timestamp index, and one explicit subscribe/app CTA line to descriptions; give Shorts at least one keyword line.',
  },
  {
    id: 'RC-PILLAR-NARROW',
    label: 'Channel identity is split across pillars',
    members: ['E2'],
    deliverable:
      'Foreground the single pillar most aligned with the goal in shelf order and About; re-shelve the rest as secondary.',
  },
  {
    id: 'RC-SHORTS-HOOK-FORMAT',
    label: 'Shorts abandoned the proven hook format',
    members: ['E3'],
    deliverable:
      'Restore the curiosity-fact Shorts format and cadence; move app-CTA out of the title into pinned comment/description.',
  },
  {
    id: 'RC-CADENCE',
    label: 'Publishing cadence gaps',
    members: ['E1'],
    deliverable:
      'Re-establish a consistent upload rhythm (incl. Shorts) before/through the campaign so engagement signals stay live.',
  },
  {
    id: 'RC-ENGAGEMENT-NO-REPLIES',
    label: 'High-intent comments left unworked',
    members: ['G1'],
    deliverable:
      'Pin a creator comment with a question on the top videos within 24h of posting to seed first-comment momentum.',
  },
  {
    id: 'RC-OBJECTIVES-INCONSISTENT',
    label: 'Channel goals aren’t reinforced consistently',
    members: ['H1'],
    deliverable:
      'Make every surface (About, each description, trailer, banner) lead to the stated objectives in priority order — e.g. give the app a fixed line in every description and the About top, and a spot in the trailer.',
  },
  {
    id: 'RC-SERIES-STYLE',
    label: 'Series identity is fragmented for the campaign',
    members: ['H2'],
    deliverable:
      'Keep in-series consistency, but pick one strongest series as the campaign’s creative face and build the ad thumbnails/titles around that single recognisable identity.',
  },
];

/** The root cause a given criterion rolls up into (if any). */
export const rcForCriterion = (id: string): RootCause | undefined =>
  ROOT_CAUSES.find((rc) => rc.members.includes(id));
